import { useMemo } from "react";
import SaltoLocksView from "./SaltoLocksView";
import { useAuthContext } from "../../../../context/AuthContext";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";

function SaltoLocks({ configData, setConfigData, saltoResources }) {
  const authContext = useAuthContext();
  const callEvaApi = useEvaApi();

  const lockLabels = useMemo(() => {
    return saltoResources.locks.reduce((labels, lock) => {
      labels[lock.id] = lock.label;
      return labels;
    }, {});
  }, [saltoResources.locks]);

  const availableLocks = useMemo(() => {
    return saltoResources.locks.filter((lock) => {
      return !configData.locks.some(
        (configLock) => configLock.lock_id === lock.id,
      );
    });
  }, [saltoResources.locks, configData]);

  /**
   * This method will add a lock to the config data
   * @param {string} lock_id
   */
  function onLockAdd(lock_id) {
    setConfigData((prev) => ({
      ...prev,
      locks: [
        ...prev.locks,
        {
          lock_id,
          location_id: authContext.auth?.user?.company?.locations?.[0]?.id,
          entry_type: "entrance_exit",
        },
      ],
    }));
  }

  /**
   * This method will update the location id on select change
   * @param {string} lock_id
   * @param {int} location_id
   */
  function onLockLocationChange(lock_id, location_id) {
    setConfigData((prev) => ({
      ...prev,
      locks: prev.locks.map((lock) =>
        lock.lock_id === lock_id ? { ...lock, location_id } : lock,
      ),
    }));
  }

  /**
   * This method will update the entry type on select change
   * @param {string} lock_id
   * @param {string} entry_type
   */
  function onLockEntryTypeChange(lock_id, entry_type) {
    setConfigData((prev) => ({
      ...prev,
      locks: prev.locks.map((lock) =>
        lock.lock_id === lock_id ? { ...lock, entry_type } : lock,
      ),
    }));
  }

  /**
   * This method will delete a lock from the config data
   * @param {string} lock_id
   */
  function onLockDelete(lock_id) {
    setConfigData((prev) => ({
      ...prev,
      locks: prev.locks.filter((lock) => lock.lock_id !== lock_id),
    }));
  }

  /**
   * This method will open a lock
   * @param {string} lock_id
   */
  async function onLockOpen(lock_id) {
    await callEvaApi(
      new EvaApiCall(`integrations/salto/locks/${lock_id}/open`)
        .setLoadingGroup(`salto_lock_open_${lock_id}`)
        .setMethod("POST")
        .setAlertError(true)
        .setAlertSuccess(true),
    );
  }

  return (
    <SaltoLocksView
      configData={configData}
      availableLocks={availableLocks}
      lockLabels={lockLabels}
      onLockAdd={onLockAdd}
      onLockLocationChange={onLockLocationChange}
      onLockEntryTypeChange={onLockEntryTypeChange}
      onLockDelete={onLockDelete}
      onLockOpen={onLockOpen}
    />
  );
}

export default SaltoLocks;
